<template>
  <div class="contain">
    <!-- 검색 영역 -->
    <div class="search_form" ref="search_form">
      <el-form
        :inline="true"
        :label-position="$device.mobile ? `top` : `left`"
        label-suffix=":"
        :size="size"
        @submit.native.prevent>
        <el-row>
          <el-col :md="12">
            <el-form-item :label="$t('대상 매장')">
              <span class="dm-scope">{{ scopeStoreName(storeCd) }}</span>
              <span class="dm-weak">&nbsp;{{ $t('— 이 매장이 속한 샵인샵 그룹 전체를 봅니다') }}</span>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <div class="buttons" ref="buttons">
      <el-button :size="size" @click="search" icon="el-icon-search">{{ $t('검색') }}</el-button>
    </div>

    <el-alert
      class="dm-hint"
      type="info"
      :closable="false"
      show-icon
      :title="
        $t(
          'POS 가 배달앱 계정에서 발견한 가게 목록입니다. 매장이 정해지지 않은 가게는 매출·메뉴가 어디에도 잡히지 않습니다.'
        )
      ">
    </el-alert>

    <!-- 리스트 -->
    <!--
      **서버가 정한 순서를 뒤집지 않는다** (§8.2).

      응답은 `미매핑 먼저 → 마지막 발견 최신순` 으로 이미 정렬돼 있다. 사장님이 겪는 문제 대부분이
      "왜 이 가게 매출이 안 잡히지"이고 답은 거의 미매핑이라, 그 행이 맨 위에 있는 것이 이 화면의
      요점이다. 열에 정렬을 붙이면 그 요점이 한 번의 클릭으로 사라진다.
    -->
    <el-table
      :height="tableHeight"
      ref="table"
      border
      stripe
      tooltip-effect="dark"
      :data="tableData"
      :size="size"
      :empty-text="$t('POS 가 보고한 배달앱 가게가 아직 없습니다.')">
      <el-table-column
        :label="tableData.length.toString()"
        type="index"
        width="55"
        align="center"
        label-class-name="data_cnt"></el-table-column>
      <el-table-column :label="$t('플랫폼')" width="100">
        <template slot-scope="s">{{ platformName(s.row.platformGroup) }}</template>
      </el-table-column>
      <!--
        **`가게` 는 `이름(번호)` 한 줄이다** (§16.179) — 표기 규칙은 utils/deliveryMenuSync 가 소유한다.
        열 이름도 형제 화면 셋과 같은 `가게` 다. 설계 문서의 `샵` 은 코드 어휘(`shopNo`)이지 사장님
        말이 아니라, 이 화면만 다른 낱말을 쓰면 같은 것을 두 이름으로 부르게 된다.
      -->
      <el-table-column :label="$t('가게')" min-width="220" show-overflow-tooltip>
        <template slot-scope="s">{{ shop(s.row) }}</template>
      </el-table-column>
      <!--
        **미매핑 행을 숨기지 않고, 눈에 띄게 둔다** (§8.2).

        `미분류` 는 빈 칸이 아니라 선택지의 이름이기도 하다 — 귀속을 지우는 것이 이 축의 정당한 값이다.
      -->
      <el-table-column :label="$t('귀속 매장')" width="240">
        <template slot-scope="s">
          <el-select
            :value="s.row.storeCd"
            :size="size"
            :disabled="!canBind || saving"
            class="dm-shopmap__store"
            @change="changeStore(s.row, $event)">
            <el-option :label="$t(unassignedText)" :value="null"></el-option>
            <el-option
              v-for="store in stores"
              :key="store.storeCd"
              :label="store.storeNm"
              :value="store.storeCd"></el-option>
          </el-select>
          <el-tag v-if="!s.row.mapped" size="mini" type="warning" class="dm-shopmap__tag">{{ $t('미매핑') }}</el-tag>
        </template>
      </el-table-column>
      <!--
        **마지막 발견 시각은 판단 근거다** (§8.2).

        오래된 행은 그 계정에서 이미 사라진 샵일 수 있다 — 귀속을 지우기 전에 사장님이 볼 수 있는
        유일한 근거이므로 장식이 아니다. 표기는 네 화면이 같은 한 곳을 지난다 (§16.167).
      -->
      <el-table-column :label="$t('마지막 발견')" width="150">
        <template slot-scope="s">{{ formatDate(s.row.observedAt) }}</template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import deliveryMenuScopeMixin from '@/mixins/deliveryMenuScopeMixin'
import { util } from '@/mixins/utilMixin'
import { initData } from '@/utils/constants'
import {
  DELIVERY_MENU_ACTIONS,
  DELIVERY_SHOP_MAP_API,
  SHOP_MAP_REQUEST_CONFIG,
  SHOP_MAP_UNASSIGNED_TEXT,
  deliveryMenuErrorMessage,
  deliveryMenuPlatformName,
  deliveryMenuTimeText,
  shopMapAssignFailureText,
  shopMapAssignSummary,
  shopMapImpactFailureText,
  shopMapMoveConfirmText,
  shopMapNeedsConfirm,
  shopText,
} from '@/utils/deliveryMenuSync'
import { checkAuthActionAccess } from '@/utils/checkAuthActionAccess'

/**
 * 배달 샵 ↔ 매장 매핑 — `GET /shop-map` · `GET /shop-map/impact` · `POST /shop-map`.
 *
 * 설계: `ordern-workspace/docs/delivery-menu/delivery-shop-map-server-ssot-design.md` §8.2·§8.3.
 *
 * 행 모델은 **단말이 보고한 샵 전부**다 — 매핑 여부와 무관하다. 매핑된 것만 보여 주면 이 화면이
 * 답해야 할 질문("왜 이 가게 매출이 안 잡히지")의 답이 화면에 없다.
 *
 * **한 매장에 같은 배달앱 가게 여럿을 걸 수 있다** (N:1, 2026-10-27 — 워크스페이스
 * `docs/planning/delivery-shop-map-n1-20261027.md`). 매핑된 가게는 메뉴도 모두 그 매장으로 가져오므로
 * 고를 「메뉴 원천」이 없다 — 서버가 옛 POS 판을 위해 아직 내리는 `menuSourceYn` 은 읽지 않는다.
 *
 * **파급은 화면이 세지 않는다** (§8.3). 같은 수를 두 곳에서 세면 다이얼로그가 말한 건수와 실제
 * 언바인드 수가 갈리고, 그 차이는 사장님이 되돌린 뒤에야 드러난다. 그래서 `/impact` 를 누르기 전에
 * 부르고, 저장 응답(같은 모양)으로 결과를 다시 확인시킨다.
 *
 * **표의 입력 컨트롤은 `v-model` 이 아니라 `:value` + `@change` 다.** 이 화면에서 값이 인정되는
 * 시점은 클릭이 아니라 **서버 응답**이다 — `v-model` 이면 확인을 취소하거나 저장이 400 으로
 * 거절돼도 화면에는 고른 값이 남아, 화면과 서버가 두 말을 한다. Element 의 select 는
 * 프롭이 안 바뀌면 표시를 되돌리므로(2.15 `select.setSelected`) 취소·실패
 * 갈래에서 화면이 따로 할 일이 없다.
 */
export default {
  name: 'DeliveryMenuShopMapView',
  mixins: [mainVueMixin, deliveryMenuScopeMixin],

  data() {
    const data = initData()
    /** 귀속 후보 — 이 매장이 속한 샵인샵 그룹. 출처는 목록 응답이다(shop-in-shop 을 따로 부르지 않는다) */
    data.stores = []
    data.saving = false
    return data
  },

  computed: {
    storeCd() {
      return this.scopeStoreCd
    },
    canBind() {
      return checkAuthActionAccess(DELIVERY_MENU_ACTIONS.BIND)
    },
    unassignedText() {
      return SHOP_MAP_UNASSIGNED_TEXT
    },
  },

  async created() {
    await this.loadScopeStore()
    this.search()
  },

  mounted() {
    this.$nextTick(() => {
      this.mainVue.currVue = this
    })
  },

  methods: {
    platformName: deliveryMenuPlatformName,

    /** 샵 표기(이름 ?: 번호) 규칙은 utils/deliveryMenuSync 한 곳이 소유한다 (§16.179) */
    shop: shopText,

    formatDate(value) {
      return deliveryMenuTimeText(value, util.getDate)
    },

    /** 귀속 후보의 이름. 응답의 `stores[]` 가 유일한 출처다 */
    storeNmOf(storeCd) {
      if (storeCd == null) return SHOP_MAP_UNASSIGNED_TEXT
      const hit = this.stores.find((store) => Number(store.storeCd) === Number(storeCd))
      return (hit && hit.storeNm) || this.scopeStoreName(storeCd)
    },

    async search() {
      if (this.storeCd == null) return
      try {
        const res = await this.$axios.get(DELIVERY_SHOP_MAP_API, { params: { storeCd: this.storeCd } })
        const data = res.data || {}
        // **서버가 정렬해 내려준 순서 그대로다** — 미매핑이 맨 위인 것이 이 화면의 요점이다 (§8.2)
        this.tableData = Array.isArray(data.rows) ? data.rows : []
        this.stores = Array.isArray(data.stores) ? data.stores : []
        // 매장명의 출처는 이 응답이다 — 이름을 얻으려 shop-in-shop 을 부르지 않는다 (규약 §16.152)
        this.rememberStoreNames(this.stores)
      } catch (e) {
        this.tableData = []
        this.stores = []
        this.$message({ type: 'error', message: deliveryMenuErrorMessage(e) })
      }
    },

    /**
     * 귀속 매장을 바꾼다 — **누르기 전에 파급을 묻는다** (§8.3).
     *
     * 서버는 같은 트랜잭션에서 옛 매장의 그 샵 링크를 언바인드한다(`smenu_cd` → null, `ORPHAN`).
     * 위험해서 묻는 것이 아니라(거의 언제나 오설정 정정이고 링크가 떨어지는 것은 의도한 결과다)
     * **되돌리는 데 드는 품**을 알려야 하기 때문에 묻는다 — 실측에서 한 샵이 113건이었다.
     *
     * `unbindLinks` 가 0 이면 묻지 않는다. 뗄 것이 없는 조작에까지 확인을 붙이면 확인이 소음이 되고,
     * 정작 113건이 떨어지는 순간의 확인도 같은 무게로 읽힌다.
     */
    async changeStore(row, nextStoreCd) {
      const next = nextStoreCd == null ? null : Number(nextStoreCd)
      const current = row.storeCd == null ? null : Number(row.storeCd)
      if (next === current) return

      const impact = await this.loadImpact(row, next)
      // 파급을 모르는 채로는 저장하지 않는다 — 그러면 §8.3 이 요구한 "누르기 전에" 가 없는 것과 같다
      if (impact === null) return
      if (shopMapNeedsConfirm(impact)) {
        try {
          await this.$confirm(shopMapMoveConfirmText(impact, this.shop(row), this.storeNmOf(next)), this.$t('확인'), {
            type: 'warning',
          })
        } catch (e) {
          return // 취소만 이 갈래로 온다
        }
      }

      const result = await this.postAssign({
        platformGroup: row.platformGroup,
        shopNo: row.shopNo,
        storeCd: next,
      })
      if (!result.ok) {
        this.$message({ type: 'error', message: result.message })
        return
      }
      // **예측이 아니라 응답의 수로 말한다** — 그 사이 관측이 링크를 더 만들었을 수 있다
      this.$message({
        type: 'success',
        message: shopMapAssignSummary(result.impact, this.shop(row), this.storeNmOf(next)),
      })
      this.search()
    },

    /**
     * 이 변경이 무엇을 뗄지 — **서버가 답한다** (§8.3).
     *
     * 인터셉터의 기본값 둘(오류 토스트·403 로그아웃)을 함께 끄는 것은 §16.175·§16.170 의 규칙대로다 —
     * 이 요청의 실패에는 화면이 대체 동작을 갖는다: **저장을 멈춘다.** 그것이 곧 "파급을 모르면
     * 저장하지 않는다" 이므로 실패가 조용히 지나가지 않는다. 옵트아웃과 문구는 둘 다 utils 소유다.
     */
    async loadImpact(row, nextStoreCd) {
      try {
        const res = await this.$axios.get(`${DELIVERY_SHOP_MAP_API}/impact`, {
          params: { platformGroup: row.platformGroup, shopNo: row.shopNo, storeCd: nextStoreCd },
          ...SHOP_MAP_REQUEST_CONFIG,
        })
        return res.data || {}
      } catch (e) {
        this.$message({ type: 'error', message: shopMapImpactFailureText(e) })
        return null
      }
    },

    /**
     * 저장 한 번 — **문구는 부르는 쪽이 만든다.**
     *
     * 성공 문구는 저장 응답의 수와 부르는 쪽만 아는 가게·목적 매장으로 만든다. 서버 400 문구
     * (`이 샵은 이미 다른 매장에 걸려 있습니다` 등)는 다음 행동을 담은 서버 문장이라 갈아끼우지 않고
     * 그대로 올린다 (§16.57 · §16.121). 403 만 갈래가
     * 다르다 — 세션의 끝이 아니라 이 조작의 거절이므로, 옵트아웃(`SHOP_MAP_REQUEST_CONFIG`)으로
     * 로그아웃을 막고 `shopMapAssignFailureText` 가 사장님 말로 답한다.
     */
    async postAssign(body) {
      this.saving = true
      try {
        const res = await this.$axios.post(DELIVERY_SHOP_MAP_API, body, SHOP_MAP_REQUEST_CONFIG)
        return { ok: true, impact: res.data || {} }
      } catch (e) {
        return { ok: false, message: shopMapAssignFailureText(e) }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.dm-scope {
  font-weight: 600;
}
.dm-weak {
  color: #909399;
  font-size: 12px;
}
.dm-hint {
  margin-bottom: 8px;
}
.dm-shopmap__store {
  width: 150px;
}
.dm-shopmap__tag {
  margin-left: 6px;
}
</style>
